import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { WorkflowStepInterface } from '@vendasta/automata';
import { AutomationVariableMenuComponent } from '../../../../../common/components/automation-variable-menu/automation-variable-menu.component';
import { insertVariable } from '../../../../../common/components/automation-variable-menu/automation-variable-menu.service';
import { ALL_VARIABLE_MENU_DATA_TYPES } from '../../../../../common/constants';
import {
  GenericNotificationID,
  HighPriorityAutomationNotificationID,
  HotLeadsNotificationID,
} from '../../send-notification-to-the-user/constants';
import { CommonModule } from '@angular/common';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { CapitalizePipe } from '../../../../../common/components/capitalize.pipe';
import { GalaxyWrapModule } from '@vendasta/galaxy/galaxy-wrap';
import { HtmlEditorComponent } from '../../../../../shared/html-editor.component';
import { NotificationSettingsLinkComponent } from '../../../common/shared-form-elements';
import { urlValidatorWithBusinessIdAndSalesUrlReplacement } from '../../../common/utils';

export interface EmailNotification {
  subjectTemplate: string;
  textTemplate: string;
  buttonTextTemplate: string;
  linkTemplate: string;
}

export interface InAppNotification {
  textTemplate: string;
  linkTemplate: string;
}

export interface Notifications {
  notificationChannel: string;
  emailNotification: EmailNotification;
  inAppNotification: InAppNotification;
}

@Component({
  selector: 'automata-send-notification-form',
  templateUrl: './send-notification-form.component.html',
  styleUrls: ['./send-notification-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyPipesModule,
    CapitalizePipe,
    NotificationSettingsLinkComponent,
    AutomationVariableMenuComponent,
    GalaxyWrapModule,
    HtmlEditorComponent,
  ],
})
export class SendNotificationFormComponent implements OnInit {
  @Input() partnerId: string;
  @Input() step: WorkflowStepInterface;
  @Input() defaultLinkTemplate: string;
  @Input() userTypeKey: string;
  @Input() notificationMessagePlaceholderKey: string;
  @Input() notificationId: string;
  @Input() isSalesperson: boolean;

  allDataTypes = ALL_VARIABLE_MENU_DATA_TYPES;
  genericNotificationID = GenericNotificationID;
  highPriorityAutomationNotificationID = HighPriorityAutomationNotificationID;
  hotLeadsNotificationID = HotLeadsNotificationID;

  notificationChannel: UntypedFormControl = new UntypedFormControl('', Validators.required);
  inAppText: UntypedFormControl = new UntypedFormControl('', Validators.required);
  inAppLinkTemplate: UntypedFormControl = new UntypedFormControl('', [
    Validators.required,
    urlValidatorWithBusinessIdAndSalesUrlReplacement,
  ]);
  emailBody: UntypedFormControl = new UntypedFormControl('', Validators.required);
  emailLinkTemplate: UntypedFormControl = new UntypedFormControl('', [
    Validators.required,
    urlValidatorWithBusinessIdAndSalesUrlReplacement,
  ]);
  emailSubject: UntypedFormControl = new UntypedFormControl('', Validators.required);
  emailButtonText: UntypedFormControl = new UntypedFormControl('', Validators.required);
  form: UntypedFormGroup;
  editorMenu = {
    insert: { title: 'Insert', items: 'image link media variables' },
  };

  constructor(
    protected readonly translateService: TranslateService,
    protected readonly fb: UntypedFormBuilder,
  ) {}

  ngOnInit(): void {
    if (!this.notificationMessagePlaceholderKey) {
      this.notificationMessagePlaceholderKey =
        'AUTOMATIONS.EDITOR.TASKS.SEND_IN_APP_NOTIFICATION.NOTIFICATION_MESSAGE_PLACEHOLDER';
    }
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    //Need this until we can switch fully to galaxy forms
    const initialNotificationTypeId =
      jsonData?.notification_channel || this.notificationId || this.genericNotificationID;
    this.notificationChannel.setValue(initialNotificationTypeId);
    this.emailLinkTemplate.setValue(jsonData?.email_notification?.link_template || this.defaultLinkTemplate);
    this.emailSubject.setValue(jsonData?.email_notification?.subject_template || '');
    this.emailBody.setValue(jsonData?.email_notification?.text_template || '');
    this.emailButtonText.setValue(jsonData?.email_notification?.button_text_template || '');
    this.inAppLinkTemplate.setValue(jsonData?.in_app_notification?.link_template || this.defaultLinkTemplate);
    this.inAppText.setValue(jsonData?.in_app_notification?.text_template || '');
    this.form = this.fb.group({
      notificationChannel: this.notificationChannel,
      emailSubject: this.emailSubject,
      emailBody: this.emailBody,
      emailButtonText: this.emailButtonText,
      emailLinkTemplate: this.emailLinkTemplate,
      inAppText: this.inAppText,
      inAppLinkTemplate: this.inAppLinkTemplate,
    });
  }

  getData(): Notifications {
    const emailNotification = {
      subjectTemplate: this.emailSubject.value,
      textTemplate: this.emailBody.value,
      buttonTextTemplate: this.emailButtonText.value,
      linkTemplate: this.emailLinkTemplate.value,
    };
    const inAppNotification = {
      textTemplate: this.inAppText.value,
      linkTemplate: this.inAppLinkTemplate.value,
    };

    return {
      notificationChannel: this.notificationChannel.value,
      emailNotification: emailNotification,
      inAppNotification: inAppNotification,
    };
  }

  isFormValid(): boolean {
    return !this.form.invalid;
  }

  insertVariable(elementID: string, control: UntypedFormControl, variable: string): void {
    insertVariable(elementID, control, variable);
  }
}
